import { useState, useEffect } from 'react';
import { VideoScreen2 } from './VideoScreen2';
import SingleLineChart from './SingleLineChart';
import { Storage } from 'aws-amplify';
import DebugDialog from './DebugDialog';
import { getSegmentDefinition } from './getSegmentDefinition';
import { fetchAndFormatRunDetails } from './fetchAndFormatRunDetails';

// Combine these.  I think maybe formatTime changed?
import { round10, floor10 } from '../helpers';
import { roundTo } from './helpers';

import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

function SessionViewer({
  liftCreatingSession,
  selectedSession,
  liftSelectedSession,
}) {
  const [enhancedTelemetryByLapNumber, setEnhancedTelemetryByLapNumber] =
    useState({});

  const [laptimeData, setLaptimeData] = useState([]); // raceHero Lap data
  const [lapData, setLapData] = useState([]); // to replace laptimeData but current relies on it

  const [segmentDefinitions, setSegmentDefinitions] = useState([]);
  const [combinedStates, setCombinedStates] = useState({
    videoCurrentTime: 0,
    currLap: 0,
    currLapElapsedTime: 0,
    segment: 1,
    segmentDistance: 0,
  });

  // Convert YouTube URLs to proper format for video players
  const normalizeVideoUrl = (url) => {
    if (!url) return null;
    
    // Handle YouTube URLs
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      // Extract video ID from various YouTube URL formats
      let videoId = null;
      
      if (url.includes('youtu.be/')) {
        // Short format: https://youtu.be/MzaTbUcFNWQ
        videoId = url.split('youtu.be/')[1].split('?')[0].split('&')[0];
      } else if (url.includes('youtube.com/watch?v=')) {
        // Full format: https://www.youtube.com/watch?v=MzaTbUcFNWQ
        videoId = url.split('v=')[1].split('&')[0];
      } else if (url.includes('youtube.com/embed/')) {
        // Already embed format
        return url;
      }
      
      if (videoId) {
        // Return as full YouTube URL format
        return `https://www.youtube.com/watch?v=${videoId}`;
      }
    }
    
    // Return original URL for non-YouTube videos
    return url;
  };

  const videoUrl = normalizeVideoUrl(selectedSession?.videoUrl) || null;
  const telemetryFilename = selectedSession?.telemetryFilename || null;
  // Default to a generic track (sonoma) if no trackId is provided - allows manual sessions to work
  const trackId = selectedSession?.venue?.configuration?.id || 'default'; // Default fallback for manual sessions
  const videoMarkers = {
    startLineTime: floor10(selectedSession?.crossStart, -3),
  };
  const [fullLap, setFullLap] = useState(false);

  // Debug logging to check video URL
  console.log('SessionViewer Debug:', {
    originalVideoUrl: selectedSession?.videoUrl,
    convertedVideoUrl: videoUrl,
    telemetryFilename,
    trackId,
    selectedSession,
    lapDataLength: lapData?.length,
    videoMarkers
  });

  // This will load the correct segment definitions
  useEffect(() => {
    let segments;
    if (trackId === 946 || trackId === 915) {
      // if (trackId === 915) {
      segments = 'thilleastbypassSegments';
    } else {
      // Default to sonoma for manual sessions or unknown tracks
      segments = 'sonomaFullSegments';
    }
    const segs = getSegmentDefinition(segments);
    setSegmentDefinitions(segs);
  }, [trackId]);

  // download telemetry from S3 and preprocess to include lap and segment numbers
  useEffect(() => {
    async function downloadS3Telemetry() {
      const result = await Storage.get(telemetryFilename, {
        download: true,
        level: 'public', // amplify Storage is nuts going all public for now
      });
      result.Body.text().then((string) => {
        // handle the String data return String
        const theJson = JSON.parse(string);
        const allLapTelemetry = theJson;
        const numericLapData = allLapTelemetry?.lapdata?.map((obj) => {
          const newObj = {};
          for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
              newObj[key] = parseFloat(obj[key]); // Use parseFloat to convert strings to numbers
            }
          }
          return newObj;
        });
        const metaData = {
          ...allLapTelemetry.metadata,
          ...allLapTelemetry.FileDetails,
        };

        const telemetryByLapNumber = {};
        numericLapData.forEach((data) => {
          if (!telemetryByLapNumber[data.LapNumber]) {
            telemetryByLapNumber[data.LapNumber] = [];
          }
          telemetryByLapNumber[data.LapNumber].push(data);
        });

        // add in a 'SegmentNumber' key to each telemetry record
        Object.entries(telemetryByLapNumber).forEach(([lapnum, lapdata]) =>
          lapdata.forEach((data, index) => {
            // console.log(data)
            const actSegNumFoo = segmentDefinitions?.find(
              (seg) =>
                round10(data.Distance * 1000, -1) >= seg.startDistance &&
                round10(data.Distance * 1000, -1) <= seg.endDistance
            );
            const actSegNum = actSegNumFoo?.segment;
            // if (actSegNum === undefined) {
            //   console.log(
            //     lapnum,
            //     actSegNum,
            //     actSegNumFoo,
            //     round10(data.Distance * 1000, -1),
            //     index
            //   );
            //   console.log(data);
            // }
            data.SegmentNumber = actSegNum;
          })
        );
        setEnhancedTelemetryByLapNumber({
          metaData: metaData,
          laps: telemetryByLapNumber,
        });
      });
    }
    if (telemetryFilename !== null && segmentDefinitions.length > 0) {
      downloadS3Telemetry().catch(console.error);
    }
  }, [telemetryFilename, segmentDefinitions]);

  // fetch and format raceHero run details (only for RaceHero sessions, not manual)
  useEffect(() => {
    async function asyncWrapper() {
      try {
        const laptimeData = await fetchAndFormatRunDetails(
          selectedSession.runId,
          selectedSession.carNum
        );
        setLaptimeData(laptimeData || []);
      } catch (error) {
        console.error('Error fetching run details:', error);
        setLaptimeData([]);
      }
    }
    
    // Only fetch RaceHero data if we have both runId and carNum, and it's not a manual session
    // Manual sessions have UUIDs as runId (from sessionId), RaceHero has numeric runIds
    const isNumericRunId = selectedSession?.runId && !isNaN(Number(selectedSession.runId));
    
    if (selectedSession?.runId && selectedSession?.carNum && isNumericRunId) {
      asyncWrapper();
    } else {
      // For manual sessions, ensure we have empty laptimeData to trigger default lap creation
      setLaptimeData([]);
    }
  }, [selectedSession.runId, selectedSession.carNum]);

  // Pre-process the lap data
  // should call the fetchRunData stuff as a function and then pass it to the below
  // to process it.  then i don't store it in state in two places
  useEffect(() => {
    // arguments: seg_def, lap_data, start_line_time, enhanced_telemetry
    // returns: lap data with in and out laps and segment start times if applicable
    function addSegmentStartTimesToLapData(lap, seg_def) {
      const lapnum = lap.number;
      const stuff = seg_def.map((segment) => {
        const segDist = segment.startDistance / 1000;
        const segNum = segment.segment;
        // the first distance >= to the one in segmentDefinitions
        const foundRecord = enhancedTelemetryByLapNumber?.laps?.[lapnum]?.find(
          (rec) => rec.Distance >= segDist
        );
        const segStartTime = foundRecord?.Time || null;
        return [segNum, segStartTime];
      });
      const obj = Object.fromEntries(stuff);
      return obj;
    }

    function adjustLapData(lap_data, start_line_time) {
      const startlineAdjustedLapData = lap_data.map((lap) => {
        return {
          number: lap.number,
          position: lap.position,
          time: lap.time,
          timeCumm: lap.timeCumm,
          timeCummLapStart: lap.timeCummLapStart,
          adjTimeCummLapStart: round10(
            lap.timeCummLapStart + start_line_time,
            -3
          ),
        };
      });

      const startlineAdjustedLapDataEnhanced = startlineAdjustedLapData.map(
        (lap) => {
          if (Object.keys(enhancedTelemetryByLapNumber).length > 0) {
            const segmentStartTimes = addSegmentStartTimesToLapData(
              lap,
              segmentDefinitions
            );
            return { ...lap, segmentStartTimes };
          } else {
            return { ...lap };
          }
        }
      );
      
      // Guard clause to prevent crashes if no laps exist
      if (!startlineAdjustedLapDataEnhanced || startlineAdjustedLapDataEnhanced.length === 0) {
        return [];
      }
      
      const firstRunningLap = startlineAdjustedLapDataEnhanced?.slice(0, 1)[0];
      const lastRunningLap = startlineAdjustedLapDataEnhanced?.slice(-1)[0];
      
      // Safety checks for lap properties
      if (!firstRunningLap || !lastRunningLap) {
        return startlineAdjustedLapDataEnhanced;
      }
      
      const outlap = {
        number: 0, // outlap
        position: firstRunningLap.position || 0, // start position
        time: Number(roundTo(firstRunningLap.adjTimeCummLapStart || 0, 3)), // from video time zero to start of lap 1
        timeCumm: 0,
        timeCummLapStart: 0,
        adjTimeCummLapStart: 0, // video time zero
      };
      const inlap = {
        number: (lastRunningLap.number || 0) + 1, // inlap
        position: 0, // fix this finish position
        time: 0, // fix this - time between adjTimeCummLapStart and end of video (duration)
        timeCumm: 0,
        timeCummLapStart: 0,
        adjTimeCummLapStart:
          (lastRunningLap.adjTimeCummLapStart || 0) + (lastRunningLap.time || 0),
      };
      return [outlap, ...startlineAdjustedLapDataEnhanced, inlap];
    }

    // Create minimal default lap data for manual sessions without RaceHero data
    function createDefaultLapData(start_line_time) {
      return [
        {
          number: 0, // outlap
          position: 1,
          time: start_line_time || 0,
          timeCumm: 0,
          timeCummLapStart: 0,
          adjTimeCummLapStart: 0,
        },
        {
          number: 1, // default single lap
          position: 1,
          time: 120, // default 2 minute lap
          timeCumm: 120,
          timeCummLapStart: start_line_time || 0,
          adjTimeCummLapStart: start_line_time || 0,
        }
      ];
    }

    if (laptimeData.length > 0 && segmentDefinitions.length > 0) {
      // Process RaceHero lap data
      setLapData(adjustLapData(laptimeData, videoMarkers.startLineTime));
    } else if (segmentDefinitions.length > 0) {
      // For manual sessions without RaceHero data, or while RaceHero data is loading, create minimal lap structure
      setLapData(createDefaultLapData(videoMarkers.startLineTime));
    }
  }, [
    laptimeData,
    videoMarkers.startLineTime,
    segmentDefinitions,
    enhancedTelemetryByLapNumber,
  ]);

  return (
    <Grid
      container
      direction="column"
      sx={{
        minHeight: '100vh',
        height: '100vh',
        overflow: 'hidden',
        margin: 0,
        padding: 0,
      }}
    >
      {/* header */}
      <Grid
        item
        container
        alignItems="center"
        justify="center"
        sx={{ 
          minHeight: 48,
          height: 48,
          flexShrink: 0,
          px: 1
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'row-reverse',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Button
            onClick={() => liftCreatingSession(0)}
            size="small"
            variant="outlined"
          >
            Exit
          </Button>
          <DebugDialog
            combinedStates={combinedStates}
            segmentDefinitions={segmentDefinitions}
            laptimeData={laptimeData}
            lapData={lapData}
            selectedSession={selectedSession}
            enhancedTelemetryByLapNumber={enhancedTelemetryByLapNumber}
          />
        </Box>
      </Grid>
      
      {/* Main Content */}
      <Grid 
        item 
        container 
        sx={{ 
          flexGrow: 1,
          minHeight: 0, // Allow flex item to shrink below content size
          overflow: 'hidden'
        }}
      >
        {/* Video Column */}
        <Grid
          item
          container
          xs={12}
          md={Object.keys(enhancedTelemetryByLapNumber).length > 0 ? 8 : 12}
          lg={Object.keys(enhancedTelemetryByLapNumber).length > 0 ? 9 : 12}
          sx={{
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden'
          }}
        >
          {/* Video */}
          {videoUrl && lapData && lapData.length > 0 && (
            <VideoScreen2
              sessionVideoUrl={videoUrl}
              combinedStates={combinedStates}
              setCombinedStates={setCombinedStates}
              segmentDefinitions={segmentDefinitions}
              lapData={lapData}
              liftSelectedSession={liftSelectedSession}
              selectedSession={selectedSession}
              enhancedTelemetryByLapNumber={enhancedTelemetryByLapNumber}
            />
          )}
          
          {/* Show video-only mode when no lap data is available */}
          {videoUrl && (!lapData || lapData.length === 0) && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Loading session data...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Video will appear once lap data is processed.
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Charts Column */}
        {combinedStates &&
          Object.keys(enhancedTelemetryByLapNumber).length > 0 && (
            <Grid 
              item 
              xs={12} 
              md={4} 
              lg={3}
              sx={{ 
                borderLeft: { md: '1px solid #ccc' },
                borderTop: { xs: '1px solid #ccc', md: 'none' },
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  p: 1,
                  height: '100%',
                  overflow: 'auto'
                }}
              >
                {/* Status Bar */}
                <Box
                  sx={{
                    border: '2px solid orange',
                    borderRadius: 1,
                    p: 1,
                    display: 'flex',
                    flexDirection: { xs: 'row', sm: 'column', md: 'column' },
                    flexWrap: 'wrap',
                    gap: 1,
                    alignItems: 'center',
                    minHeight: 'auto',
                    flexShrink: 0
                  }}
                >
                  <Box sx={{ fontSize: '0.875rem' }}>Lap: {combinedStates.currLap}</Box>
                  <Box sx={{ fontSize: '0.875rem' }}>Segment: {combinedStates.segment}</Box>
                  <Box sx={{ fontSize: '0.875rem' }}>
                    Time: {Number(roundTo(combinedStates.currLapElapsedTime, 1))}s
                  </Box>
                  <Box sx={{ fontSize: '0.875rem' }}>
                    Dist: {Math.round(combinedStates.segmentDistance)}m
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Switch
                      onChange={() => setFullLap(!fullLap)}
                      defaultChecked
                      size="small"
                    />
                    <Box sx={{ fontSize: '0.75rem' }}>Full Lap</Box>
                  </Box>
                </Box>
                
                {/* Charts */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 1,
                  flexGrow: 1,
                  minHeight: 0
                }}>
                  <SingleLineChart
                    enhancedTelemetryByLapNumber={enhancedTelemetryByLapNumber}
                    metric={['GPS_Speed']}
                    unit={'mph'}
                    min_value={0}
                    combinedStates={combinedStates}
                    fullLap={fullLap}
                  />
                  <SingleLineChart
                    enhancedTelemetryByLapNumber={enhancedTelemetryByLapNumber}
                    metric={['Engine']}
                    unit={'rpm'}
                    min_value={0}
                    combinedStates={combinedStates}
                    fullLap={fullLap}
                  />
                  <SingleLineChart
                    enhancedTelemetryByLapNumber={enhancedTelemetryByLapNumber}
                    metric={['TPS', 'Brake_PSI']}
                    unit={'%'}
                    min_value={0}
                    combinedStates={combinedStates}
                    fullLap={fullLap}
                  />
                </Box>
              </Box>
            </Grid>
          )}
      </Grid>
      
      {/* Footer */}
      <Grid
        item
        container
        alignItems="center"
        justify="center"
        sx={{ 
          minHeight: 40,
          height: 40,
          flexShrink: 0,
          borderTop: '1px solid #eee',
          px: 1
        }}
      >
        <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
          Session Viewer
        </Box>
      </Grid>
    </Grid>
  );
}

export default SessionViewer;
